import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { PatientPackageSummary } from "@/hooks/usePatientPackages";
import i18n from "@/i18n";
import { normalizeAllowedBehaviors } from "@/lib/appointments/availability";

export type AppointmentPatientPackageMode = "new" | "existing";
export type AppointmentPatientPackageConsumptionStatus = "planned" | "consumed" | "canceled";
export type AppointmentPatientPackageBillingIntent =
  | "integral_now"
  | "partial_now"
  | "charge_later"
  | "undecided";

export interface AppointmentPatientPackageLink {
  id: string;
  appointment_id: string;
  clinic_id: string;
  pet_id: string;
  mode: AppointmentPatientPackageMode;
  patient_package_id: string | null;
  catalog_package_id: string | null;
  sessions_to_consume: number;
  consumption_status: AppointmentPatientPackageConsumptionStatus;
  billing_intent: AppointmentPatientPackageBillingIntent;
  suggested_charge_amount: number;
  consumed_at: string | null;
  created_at: string;
  updated_at: string;
  patient_package: PatientPackageSummary | null;
  catalog_package: {
    id: string;
    item_id: string;
    controls_sessions: boolean;
    default_sessions: number;
    notes: string | null;
    item: {
      id: string;
      name: string;
      sale_price: number;
      package_subtype_id?: string | null;
      package_subtype?: { name?: string | null } | null;
      allowed_appointment_types: string[];
    } | null;
  } | null;
}

export interface AppointmentPatientPackageInput {
  id?: string;
  appointmentId: string;
  clinicId: string;
  petId: string;
  mode: AppointmentPatientPackageMode;
  patientPackageId?: string | null;
  catalogPackageId?: string | null;
  sessionsToConsume?: number;
  consumptionStatus?: AppointmentPatientPackageConsumptionStatus;
  billingIntent?: AppointmentPatientPackageBillingIntent;
  suggestedChargeAmount?: number | null;
}

type RawPackageItem = {
  id: string;
  name: string;
  sale_price: number | null;
  package_subtype_id?: string | null;
  package_subtype?: { name?: string | null } | null;
  allowed_appointment_types?: Array<{ appointment_type?: string | null }> | null;
};

type RawCatalogPackage = {
  id: string;
  item_id: string;
  controls_sessions: boolean | null;
  default_sessions: number | null;
  notes: string | null;
  item: RawPackageItem | null;
};

type RawPatientPackage = {
  id: string;
  clinic_id: string;
  customer_id: string;
  pet_id: string;
  catalog_package_id: string;
  sessions_total: number | null;
  sessions_used: number | null;
  sessions_remaining: number | null;
  status_financeiro: "pendente" | "parcial" | "pago" | null;
  full_price: number | null;
  amount_paid: number | null;
  outstanding_amount: number | null;
  origin: "manual" | "appointment" | null;
  created_from_appointment_id: string | null;
  created_at: string;
  updated_at: string;
  catalog_package: RawCatalogPackage | null;
};

type RawAppointmentPatientPackageLink = {
  id: string;
  appointment_id: string;
  clinic_id: string;
  pet_id: string;
  mode: AppointmentPatientPackageMode;
  patient_package_id: string | null;
  catalog_package_id: string | null;
  sessions_to_consume: number | null;
  consumption_status: AppointmentPatientPackageConsumptionStatus | null;
  billing_intent: AppointmentPatientPackageBillingIntent | null;
  suggested_charge_amount: number | null;
  consumed_at: string | null;
  created_at: string;
  updated_at: string;
  patient_package: RawPatientPackage | null;
  catalog_package: RawCatalogPackage | null;
};

const normalizePatientPackage = (row: RawPatientPackage): PatientPackageSummary => ({
  id: row.id,
  clinic_id: row.clinic_id,
  customer_id: row.customer_id,
  pet_id: row.pet_id,
  catalog_package_id: row.catalog_package_id,
  sessions_total: row.sessions_total ?? 0,
  sessions_used: row.sessions_used ?? 0,
  sessions_remaining: row.sessions_remaining ?? Math.max((row.sessions_total ?? 0) - (row.sessions_used ?? 0), 0),
  status_financeiro: row.status_financeiro ?? "pendente",
  full_price: row.full_price ?? 0,
  amount_paid: row.amount_paid ?? 0,
  outstanding_amount: row.outstanding_amount ?? 0,
  origin: row.origin ?? "appointment",
  created_from_appointment_id: row.created_from_appointment_id ?? null,
  created_at: row.created_at,
  updated_at: row.updated_at,
  catalog_package: row.catalog_package
    ? {
      id: row.catalog_package.id,
      item_id: row.catalog_package.item_id,
      controls_sessions: row.catalog_package.controls_sessions ?? true,
      default_sessions: row.catalog_package.default_sessions ?? 1,
      notes: row.catalog_package.notes ?? null,
      item: row.catalog_package.item
        ? {
          id: row.catalog_package.item.id,
          name: row.catalog_package.item.name,
          sale_price: row.catalog_package.item.sale_price ?? 0,
          allowed_appointment_types: normalizeAllowedBehaviors(
            row.catalog_package.item.allowed_appointment_types?.map((entry) => entry.appointment_type ?? null) ?? [],
          ),
        }
        : null,
    }
    : null,
});

const normalizeLink = (row: RawAppointmentPatientPackageLink): AppointmentPatientPackageLink => ({
  id: row.id,
  appointment_id: row.appointment_id,
  clinic_id: row.clinic_id,
  pet_id: row.pet_id,
  mode: row.mode,
  patient_package_id: row.patient_package_id ?? null,
  catalog_package_id: row.catalog_package_id ?? null,
  sessions_to_consume: row.sessions_to_consume ?? 1,
  consumption_status: row.consumption_status ?? "planned",
  billing_intent: row.billing_intent ?? "undecided",
  suggested_charge_amount: row.suggested_charge_amount ?? 0,
  consumed_at: row.consumed_at ?? null,
  created_at: row.created_at,
  updated_at: row.updated_at,
  patient_package: row.patient_package ? normalizePatientPackage(row.patient_package) : null,
  catalog_package: row.catalog_package
    ? {
      id: row.catalog_package.id,
      item_id: row.catalog_package.item_id,
      controls_sessions: row.catalog_package.controls_sessions ?? true,
      default_sessions: row.catalog_package.default_sessions ?? 1,
      notes: row.catalog_package.notes ?? null,
      item: row.catalog_package.item
        ? {
          id: row.catalog_package.item.id,
          name: row.catalog_package.item.name,
          sale_price: row.catalog_package.item.sale_price ?? 0,
          allowed_appointment_types: normalizeAllowedBehaviors(
            row.catalog_package.item.allowed_appointment_types?.map((entry) => entry.appointment_type ?? null) ?? [],
          ),
        }
        : null,
    }
    : null,
});

const APPOINTMENT_PATIENT_PACKAGE_SELECT = `
  id,
  appointment_id,
  clinic_id,
  pet_id,
  mode,
  patient_package_id,
  catalog_package_id,
  sessions_to_consume,
  consumption_status,
  billing_intent,
  suggested_charge_amount,
  consumed_at,
  created_at,
  updated_at,
  patient_package:patient_packages(
    id,
    clinic_id,
    customer_id,
    pet_id,
    catalog_package_id,
    sessions_total,
    sessions_used,
    sessions_remaining,
    status_financeiro,
    full_price,
    amount_paid,
    outstanding_amount,
    origin,
    created_from_appointment_id,
    created_at,
    updated_at,
    catalog_package:catalog_packages(
      id,
      item_id,
      controls_sessions,
      default_sessions,
      notes,
      item:items(id, name, sale_price, package_subtype_id, package_subtype:package_subtypes(name), allowed_appointment_types:item_allowed_appointment_types(appointment_type))
    )
  ),
  catalog_package:catalog_packages(
    id,
    item_id,
    controls_sessions,
    default_sessions,
    notes,
    item:items(id, name, sale_price, package_subtype_id, package_subtype:package_subtypes(name), allowed_appointment_types:item_allowed_appointment_types(appointment_type))
  )
`;

export const useAppointmentPatientPackages = (appointmentId?: string | null) => {
  return useQuery({
    queryKey: ["appointment-patient-packages", appointmentId],
    enabled: Boolean(appointmentId),
    queryFn: async (): Promise<AppointmentPatientPackageLink[]> => {
      if (!appointmentId) return [];

      const { data, error } = await supabase
        .from("appointment_patient_packages")
        .select(APPOINTMENT_PATIENT_PACKAGE_SELECT)
        .eq("appointment_id", appointmentId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return ((data ?? []) as unknown as RawAppointmentPatientPackageLink[]).map(normalizeLink);
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};

export const useReplaceAppointmentPatientPackages = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: { appointmentId: string; links: AppointmentPatientPackageInput[] }) => {
      const { appointmentId, links } = input;

      const { error: deleteError } = await supabase
        .from("appointment_patient_packages")
        .delete()
        .eq("appointment_id", appointmentId)
        .eq("consumption_status", "planned");

      if (deleteError) throw deleteError;

      if (links.length === 0) {
        return [] as AppointmentPatientPackageLink[];
      }

      const payload = links.map((link) => ({
        appointment_id: link.appointmentId,
        clinic_id: link.clinicId,
        pet_id: link.petId,
        mode: link.mode,
        patient_package_id: link.mode === "existing" ? (link.patientPackageId ?? null) : null,
        catalog_package_id: link.mode === "new" ? (link.catalogPackageId ?? null) : null,
        sessions_to_consume: Math.max(1, Math.floor(link.sessionsToConsume ?? 1)),
        consumption_status: link.consumptionStatus ?? "planned",
        billing_intent: link.billingIntent ?? "undecided",
        suggested_charge_amount: Math.max(0, Number(link.suggestedChargeAmount ?? 0)),
      }));

      const { data, error } = await supabase
        .from("appointment_patient_packages")
        .insert(payload)
        .select(APPOINTMENT_PATIENT_PACKAGE_SELECT)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return ((data ?? []) as unknown as RawAppointmentPatientPackageLink[]).map(normalizeLink);
    },
    onSuccess: (result, variables) => {
      const appointmentId = result[0]?.appointment_id ?? variables.appointmentId;
      queryClient.invalidateQueries({ queryKey: ["appointment-patient-packages", appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointment", appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["patient-packages"] });
    },
    onError: (error: Error) => {
      toast({
        title: i18n.t("calendar:phase8.appointmentPatientPackages.replaceErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useCommitAppointmentPatientPackages = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (appointmentId: string) => {
      const { error } = await supabase.rpc("commit_appointment_patient_package", {
        p_appointment_id: appointmentId,
      });
      if (error) throw error;
      return appointmentId;
    },
    onSuccess: (appointmentId) => {
      queryClient.invalidateQueries({ queryKey: ["appointment-patient-packages", appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointment", appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment-financial", appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["patient-packages"] });
    },
    onError: (error: Error) => {
      toast({
        title: i18n.t("calendar:phase8.appointmentPatientPackages.commitErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
